import { useState, useEffect } from "react";
import { Plus, AlertCircle, Loader2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import WorkflowService from "../../services/workflow.service";
import { useOrganizations } from "../../hooks/useOrganizations";

const TriggerCard = ({ trigger, onToggle, onEdit }) => {
  const getTriggerIcon = (type) => {
    const icons = {
      shopify: "□",
      webhook: "⚡",
      schedule: "○",
      whatsapp: "◐",
      api_call: "◇",
      manual: "○",
    };
    return icons[type] || "○";
  };

  const getTriggerColor = (type) => {
    const colors = {
      shopify: "text-emerald-400",
      webhook: "text-orange-400",
      schedule: "text-blue-400",
      whatsapp: "text-teal-400",
      api_call: "text-cyan-400",
      manual: "text-gray-400",
    };
    return colors[type] || "text-gray-400";
  };

  return (
    <div className="p-4 bg-gray-900/30 border border-gray-800 rounded-lg hover:border-gray-700 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={`text-xs border ${getTriggerColor(trigger.type)} bg-transparent`}>
              <span className="mr-1">{getTriggerIcon(trigger.type)}</span>
              {trigger.type.charAt(0).toUpperCase() + trigger.type.slice(1)}
            </Badge>
            <h4 className="text-sm font-medium text-white">{trigger.name}</h4>
          </div>
          <p className="text-xs text-gray-500 mt-1">{trigger.description}</p>
          {trigger.url && (
            <code className="block mt-2 text-xs text-gray-400 bg-gray-950 px-2 py-1 rounded font-mono">
              {trigger.url}
            </code>
          )}
        </div>

        <div className="flex items-center gap-3 ml-4">
          <button
            onClick={() => onToggle(trigger.id, !trigger.enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              trigger.enabled ? "bg-emerald-600" : "bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                trigger.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>

          <button
            onClick={() => onEdit(trigger.id)}
            className="text-gray-400 hover:text-gray-300 text-xs"
          >
            •••
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TriggersPanel({ workflowId }) {
  const { currentAccount } = useOrganizations();
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workflowId || !currentAccount?.id) return;

    const fetchTriggers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await WorkflowService.listTriggersByWorkflow(workflowId);

        if (result.success) {
          const triggersData = result.data.triggers || [];
          
          // Transform API data to match UI expectations
          const transformedTriggers = triggersData.map(trigger => {
            // Safely extract string values
            const safeName = (value) => {
              if (!value) return '';
              if (typeof value === 'string') return value;
              if (typeof value === 'object' && value.name) return value.name;
              return String(value);
            };

            return {
              id: trigger.trigger_id || trigger.id,
              type: safeName(trigger.trigger_type || 'manual'),
              name: safeName(trigger.name || `${trigger.trigger_type || 'manual'} trigger`),
              description: safeName(trigger.description || getDefaultDescription(trigger)),
              url: safeName(trigger.webhook_url || trigger.url),
              enabled: trigger.is_active !== false
            };
          });
          
          setTriggers(transformedTriggers);
        } else {
          setError(result.error || 'Failed to load triggers');
        }
      } catch (err) {
        console.error("Failed to fetch triggers:", err);
        setError(err.message || 'Failed to load triggers');
      } finally {
        setLoading(false);
      }
    };

    fetchTriggers();
  }, [workflowId, currentAccount?.id]);

  const getDefaultDescription = (trigger) => {
    const type = trigger.trigger_type;
    if (type === 'scheduled') return trigger.cron_expression || 'Scheduled trigger';
    if (type === 'webhook') return trigger.webhook_url || 'Webhook trigger';
    if (type === 'shopify') return trigger.shop_domain || 'Shopify event trigger';
    if (type === 'whatsapp') return 'WhatsApp message trigger';
    return 'Trigger configuration';
  };

  const handleToggle = async (id, enabled) => {
    try {
      if (enabled) {
        await WorkflowService.activateTrigger(id);
      } else {
        await WorkflowService.deactivateTrigger(id);
      }
      // Update local state
      setTriggers(prev => prev.map(t => t.id === id ? { ...t, enabled } : t));
    } catch (err) {
      console.error('Failed to toggle trigger:', err);
      setError('Failed to update trigger status');
    }
  };

  const handleEdit = (id) => {
    console.log("Edit trigger:", id);
    // TODO: Implement edit modal
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <p className="text-sm text-gray-400">
          {triggers.filter(t => t.enabled).length} active triggers — every one of these can start this workflow.
        </p>
      </div>

      <div className="flex items-center justify-end mb-4">
        <Button className="bg-primary hover:bg-violet-700 text-white text-xs">
          <Plus className="w-3 h-3 mr-1.5" />
          Add trigger
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div className="text-sm text-red-400">{error}</div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            <div className="text-sm text-gray-500 font-medium">Loading triggers...</div>
          </div>
        </div>
      ) : triggers.length === 0 ? (
        <div className="text-center py-16 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5">
          <div className="text-sm text-gray-500 font-medium">No triggers configured</div>
          <p className="text-xs text-gray-600 mt-2">Add a trigger to start executing this workflow</p>
        </div>
      ) : (
        <div className="space-y-3">
          {triggers.map(trigger => (
            <TriggerCard
              key={trigger.id}
              trigger={trigger}
              onToggle={handleToggle}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}
